import { useCallback, useRef, useState } from "react";

export interface SearchGate<T> {
  // The snapshot the report should actually fetch from. `null` until the first Search — feed it
  // straight to a query hook (they treat `null` as "don't fetch"), so nothing hits the API until
  // the user submits.
  submitted: T | null;
  // A Search has been run at least once.
  hasRun: boolean;
  // The live draft differs from the last submitted snapshot (always true before the first run),
  // i.e. there are pending changes the user hasn't searched for yet.
  isDirty: boolean;
  // Snapshot the current draft into `submitted`, triggering the fetch.
  search: () => void;
}

// Defers report execution behind an explicit Search button. `draft` is the live set of query
// inputs (filters + page controls like group-by dimension, row limit, compare mode). Changing the
// draft only updates the on-screen controls; calling search() snapshots it into `submitted`, and
// only that snapshot drives fetching. This is what lets the user set the event, group-by, dates and
// other filters first, then run one request — instead of firing a call on every dropdown change.
export function useSearchGate<T>(draft: T): SearchGate<T> {
  const [submitted, setSubmitted] = useState<T | null>(null);

  // Keep the latest draft in a ref so search() always snapshots the current values without
  // needing to be re-created (and re-bound) on every keystroke.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const search = useCallback(() => setSubmitted(draftRef.current), []);

  const draftKey = JSON.stringify(draft);
  const submittedKey = submitted === null ? null : JSON.stringify(submitted);

  return {
    submitted,
    hasRun: submitted !== null,
    isDirty: submittedKey === null || submittedKey !== draftKey,
    search,
  };
}
